import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { api, BACKEND_URL } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { BidHealthBadge, CompetitivePositionBar } from '../components/BidHealth';
import { 
  ArrowLeft, 
  Loader2, 
  AlertTriangle,
  Download,
  Share2,
  Check,
  Info,
  MessageSquare,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { FeedbackModal } from '../components/FeedbackModal';

export default function BidDetailPage() {
  const { id } = useParams();
  const [bid, setBid] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [supportModal, setSupportModal] = useState(false);
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome_status: '', realised_margin_pct: '', cost_overrun_pct: '', overrun_category: null });
  const [savingOutcome, setSavingOutcome] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bidRes, analysisRes] = await Promise.all([
          api.get(`/bids/${id}`),
          api.get(`/analysis/bid/${id}`)
        ]);
        setBid(bidRes.data);
        setAnalysis(analysisRes.data);

        // Load outcome if exists
        try {
          const outcomeRes = await api.get(`/bids/${id}/outcome`);
          const o = outcomeRes.data?.outcome;
          if (o) {
            setOutcome(o);
            setOutcomeForm({
              outcome_status: o.outcome_status || '',
              realised_margin_pct: o.realised_margin_pct ?? '',
              cost_overrun_pct: o.cost_overrun_pct ?? '',
            });
          }
        } catch { /* no outcome yet */ }
        
        // Trigger feedback modal on first org analysis
        if (analysisRes.data?.org_analysis_count === 1) {
          const feedbackDismissed = localStorage.getItem('bidsentry_feedback_shown');
          if (!feedbackDismissed) {
            setTimeout(() => setShowFeedback(true), 1500);
            localStorage.setItem('bidsentry_feedback_shown', 'true');
          }
        }
      } catch (error) {
        console.error('Failed to fetch bid', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const handleShareReport = async () => {
    setSharing(true);
    try {
      const response = await api.post(`/bids/${id}/share`);
      const { share_url, token: shareToken } = response.data;
      const url = share_url || `${window.location.origin}/report/share/${shareToken}`;
      await navigator.clipboard.writeText(url);
      setShared(true);
      toast.success('Share link copied to clipboard!');
      setTimeout(() => setShared(false), 3000);
    } catch (error) {
      console.error('Failed to create share link', error);
      toast.error('Failed to generate share link');
    } finally {
      setSharing(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/analysis/bid/${id}/report`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BidSentry_Report_${bid?.bid_ref || 'bid'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) return;
    setSubmittingTicket(true);
    try {
      await api.post('/support/tickets', {
        subject: supportSubject,
        message: supportMessage,
        bid_id: id,
        analysis_id: analysis?.id || null,
      });
      toast.success('Support ticket created — we\'ll get back to you soon');
      setSupportModal(false);
      setSupportSubject('');
      setSupportMessage('');
    } catch {
      toast.error('Failed to create ticket');
    }
    setSubmittingTicket(false);
  };

  const saveOutcome = async () => {
    if (!outcomeForm.outcome_status) { toast.error('Please select Won or Lost'); return; }
    setSavingOutcome(true);
    try {
      const body = { outcome_status: outcomeForm.outcome_status };
      if (outcomeForm.realised_margin_pct !== '') body.realised_margin_pct = parseFloat(outcomeForm.realised_margin_pct);
      if (outcomeForm.cost_overrun_pct !== '') body.cost_overrun_pct = parseFloat(outcomeForm.cost_overrun_pct);
      await api.patch(`/bids/${id}/outcome`, body);
      toast.success('Project outcome saved');
      const res = await api.get(`/bids/${id}/outcome`);
      setOutcome(res.data?.outcome || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save outcome');
    }
    setSavingOutcome(false);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AppLayout>
    );
  }

  if (!bid) {
    return (
      <AppLayout>
        <div className="text-center py-16">
          <p className="text-slate-500">Bid not found</p>
          <Link to="/app/bids">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Bids
            </Button>
          </Link>
        </div>
      </AppLayout>
    );
  }

  const { margin_result, sensitivity_result, risk_assessment, bid_health, competitive_position, underbid_flags, executive_commentary, trade_profile } = analysis || {};

  // Trade fallback banner
  const tradeIsFallback = trade_profile?.config_source === 'org_settings' || !bid?.trade_type;
  const tradeName = trade_profile?.trade_name || 'Electrical Contracting';

  // Prepare sensitivity chart data
  const sensitivityData = sensitivity_result?.variations?.filter(v => v.type === 'materials').map(v => ({
    name: `${v.variation_pct > 0 ? '+' : ''}${v.variation_pct}%`,
    margin: v.margin_pct,
    type: 'materials'
  })) || [];

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="bid-detail-container">
        {/* Trade Fallback Banner */}
        {tradeIsFallback && analysis && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-sky-500/10 border border-sky-500/20 rounded-sm" data-testid="trade-fallback-banner">
            <Info className="w-4 h-4 text-sky-400 flex-shrink-0" />
            <p className="text-xs text-sky-300">
              Trade not explicitly set — using <span className="font-semibold">{tradeName}</span> defaults.
            </p>
          </div>
        )}

        {/* Header with Bid Health Badge */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex items-center gap-4">
            <Link to="/app/bids">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="page-title">{bid.bid_ref}</h1>
              <p className="text-slate-500">{bid.client_name}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="capitalize">{bid.status}</Badge>
                {bid.outcome && (
                  <Badge variant={bid.outcome === 'won' ? 'default' : 'secondary'}>
                    {bid.outcome}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-4">
            {/* BID HEALTH BADGE - Primary Visual Anchor */}
            {bid_health && (
              <div className="flex flex-col items-end gap-1">
                <BidHealthBadge
                  score={bid_health.score}
                  level={bid_health.level}
                  color={bid_health.color}
                  interpretation={bid_health.interpretation}
                  size="large"
                />
                {bid_health.risk_classification && (
                  <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm ${
                    bid_health.color === 'green' ? 'bg-green-100 text-green-800' :
                    bid_health.color === 'amber' ? 'bg-amber-100 text-amber-800' :
                    bid_health.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`} data-testid="risk-classification-badge">
                    {bid_health.risk_classification}
                  </span>
                )}
              </div>
            )}
            
            {/* Share, Download, & Support Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => setSupportModal(true)}
                variant="outline"
                data-testid="ask-about-analysis-btn"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Ask about this
              </Button>
              <Button
                onClick={handleShareReport}
                variant="outline"
                disabled={sharing}
                data-testid="share-report-btn"
              >
                {sharing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : shared ? (
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                ) : (
                  <Share2 className="w-4 h-4 mr-2" />
                )}
                {shared ? 'Link Copied!' : 'Share Report'}
              </Button>
              <Button 
                onClick={handleDownloadReport}
                variant="outline"
                disabled={downloading}
                data-testid="download-report-btn"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* DECISION GUIDANCE — surfaces recommendation above all metrics */}
        {analysis && (
          <DecisionGuidancePanel
            marginResult={margin_result}
            riskAssessment={risk_assessment}
            bidHealth={bid_health}
            executiveCommentary={executive_commentary}
            underbidFlags={underbid_flags}
            sensitivityResult={sensitivity_result}
            bid={bid}
          />
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-industrial">
            <CardContent className="pt-6">
              <p className="kpi-label">Submitted Price</p>
              <p className="kpi-value">£{bid.submitted_price?.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardContent className="pt-6">
              <p className="kpi-label">Gross Margin</p>
              <p className={`kpi-value ${
                margin_result?.gross_margin_pct >= 15 ? 'text-green-600' :
                margin_result?.gross_margin_pct >= 0 ? 'text-orange-500' : 'text-red-600'
              }`}>
                {margin_result?.gross_margin_pct?.toFixed(1)}%
              </p>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardContent className="pt-6">
              <p className="kpi-label">Profit</p>
              <p className={`kpi-value ${margin_result?.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                £{margin_result?.profit?.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardContent className="pt-6">
              <p className="kpi-label">Risk Spread</p>
              <p className="kpi-value">
                ±{sensitivity_result?.risk_spread?.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Competitive Position - Visual Band */}
        {competitive_position && (
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="section-title text-lg">Competitive Position</CardTitle>
            </CardHeader>
            <CardContent>
              <CompetitivePositionBar
                position={competitive_position.position}
                positionPercent={competitive_position.position_percent}
                zone={competitive_position.zone}
                zoneLabel={competitive_position.zone_label}
                deviationText={competitive_position.deviation_text}
              />
            </CardContent>
          </Card>
        )}

        {/* Executive Commentary — director-grade, deterministic */}
        {executive_commentary && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-6" data-testid="executive-commentary">
            <h2 className="text-xs font-bold text-slate-900 uppercase tracking-[0.15em] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              Executive Commentary
            </h2>
            <div className="space-y-3">
              {executive_commentary.paragraphs?.map((p, i) => (
                <p key={i} className="text-sm text-slate-700 leading-relaxed font-mono tracking-tight">
                  {p}
                </p>
              ))}
            </div>
            {executive_commentary.recommendation && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs font-bold text-slate-900 uppercase tracking-[0.1em] mb-1.5" style={{ fontFamily: 'Barlow Condensed' }}>
                  Recommendation
                </p>
                <p className="text-sm text-slate-800 leading-relaxed font-medium">
                  {executive_commentary.recommendation}
                </p>
              </div>
            )}
            <p className="text-[10px] text-slate-400 mt-4">
              Generated from deterministic financial model.
            </p>
          </div>
        )}

        {/* Underbid Flags */}
        {underbid_flags?.length > 0 && (
          <Card className="card-industrial border-orange-200 bg-orange-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                Warnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {underbid_flags.map((flag, i) => (
                  <li key={i} className="flex items-start gap-2 text-orange-800">
                    <span className={`inline-block w-2 h-2 mt-2 rounded-full ${
                      flag.severity === 'critical' ? 'bg-red-500' :
                      flag.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                    }`} />
                    <span>{flag.message}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Cost Breakdown */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="section-title text-lg">Cost Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {bid.cost_items?.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <span className="capitalize text-slate-600">{item.category}</span>
                    <span className="font-mono font-semibold">£{item.total_cost?.toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2 border-t-2 border-slate-200">
                  <span className="font-semibold text-slate-900">Total Direct Cost</span>
                  <span className="font-mono font-bold text-lg">£{margin_result?.direct_cost?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-slate-600">
                  <span>Overhead</span>
                  <span className="font-mono">£{margin_result?.overhead_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-slate-600">
                  <span>Contingency</span>
                  <span className="font-mono">£{margin_result?.contingency_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t-2 border-slate-200">
                  <span className="font-semibold text-slate-900">Total Cost</span>
                  <span className="font-mono font-bold text-lg">£{margin_result?.total_cost?.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sensitivity Analysis */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="section-title text-lg">Sensitivity (Materials)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {sensitivityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sensitivityData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        domain={['dataMin - 5', 'dataMax + 5']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#0f172a', 
                          border: 'none', 
                          borderRadius: '2px',
                          color: '#fff'
                        }}
                        formatter={(value) => [`${value}%`, 'Margin']}
                      />
                      <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                      <Bar dataKey="margin" radius={[2, 2, 0, 0]}>
                        {sensitivityData.map((entry, index) => (
                          <Cell 
                            key={index} 
                            fill={entry.margin >= 15 ? '#10b981' : entry.margin >= 0 ? '#f97316' : '#ef4444'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    No sensitivity data available
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Risk spread: ±{sensitivity_result?.risk_spread?.toFixed(1)}% margin variation
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Risk Assessment Details */}
        {risk_assessment?.assessments && (
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="section-title text-lg">Risk Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {risk_assessment.assessments.map((assessment, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium capitalize text-slate-700">
                        {assessment.module_name.replace(/_/g, ' ')}
                      </span>
                      <span className="font-mono text-sm">{assessment.score.toFixed(0)}/100</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          assessment.score <= 30 ? 'bg-green-500' :
                          assessment.score <= 60 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${assessment.score}%` }}
                      />
                    </div>
                    <ul className="mt-2 text-xs text-slate-500 space-y-1">
                      {assessment.findings.map((f, j) => (
                        <li key={j}>• {f}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Project Outcome Section */}
      {outcome && (
        <Card className="card-industrial mt-6" data-testid="project-outcome-card">
          <CardHeader>
            <CardTitle className="section-title text-lg">Project Outcome</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              {/* Won / Lost Toggle */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">Result</label>
                <div className="flex gap-2" data-testid="outcome-toggle">
                  {['won', 'lost'].map(status => (
                    <button
                      key={status}
                      onClick={() => setOutcomeForm(f => ({ ...f, outcome_status: status }))}
                      className={`flex-1 py-2.5 px-3 rounded-sm text-sm font-bold border transition-all ${
                        outcomeForm.outcome_status === status
                          ? status === 'won'
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-red-50 border-red-300 text-red-700'
                          : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                      }`}
                      data-testid={`outcome-${status}-btn`}
                    >
                      {status === 'won' ? 'Won' : 'Lost'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Realised Margin % */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">Realised Margin %</label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 14.5"
                  value={outcomeForm.realised_margin_pct}
                  onChange={(e) => setOutcomeForm(f => ({ ...f, realised_margin_pct: e.target.value }))}
                  className="text-sm"
                  data-testid="realised-margin-input"
                />
              </div>

              {/* Cost Overrun */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">Cost Overrun</label>
                <div className="space-y-2">
                  <div className="flex gap-1.5" data-testid="overrun-category">
                    {[
                      { key: 'on_target', label: 'On target', value: 0 },
                      { key: 'slight', label: 'Slight', value: 3 },
                      { key: 'significant', label: 'Significant', value: 10 },
                    ].map(cat => (
                      <button
                        key={cat.key}
                        onClick={() => setOutcomeForm(f => ({ ...f, cost_overrun_pct: cat.value, overrun_category: cat.key }))}
                        className={`flex-1 py-1.5 px-1 rounded-sm text-[11px] font-medium border transition-all ${
                          outcomeForm.overrun_category === cat.key
                            ? cat.key === 'on_target'
                              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                              : cat.key === 'slight'
                                ? 'bg-amber-50 border-amber-300 text-amber-700'
                                : 'bg-red-50 border-red-300 text-red-700'
                            : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                        }`}
                        data-testid={`overrun-${cat.key}-btn`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Or enter exact %"
                    value={outcomeForm.cost_overrun_pct}
                    onChange={(e) => setOutcomeForm(f => ({ ...f, cost_overrun_pct: e.target.value, overrun_category: null }))}
                    className="text-sm"
                    data-testid="cost-overrun-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              {outcome.outcome_status !== 'pending' && (
                <p className="text-[10px] text-slate-400">
                  Last updated: {outcome.updated_at ? new Date(outcome.updated_at).toLocaleDateString() : 'at analysis'}
                </p>
              )}
              <Button
                onClick={saveOutcome}
                disabled={savingOutcome || !outcomeForm.outcome_status}
                className="bg-orange-500 hover:bg-orange-600 text-white text-sm ml-auto"
                data-testid="save-outcome-btn"
              >
                {savingOutcome ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save Outcome
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Feedback Modal — triggers on first org analysis */}
      <FeedbackModal
        open={showFeedback}
        onOpenChange={setShowFeedback}
        bidId={id}
      />

      {/* Support Ticket Modal */}
      {supportModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="support-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-purple-500" /> Ask about this analysis
              </h3>
              <button onClick={() => setSupportModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {bid && (
                <div className="p-2 bg-slate-50 rounded text-[10px] text-slate-500">
                  Bid: <strong>{bid.bid_ref}</strong> — {bid.client_name}
                  {analysis && <> · Margin: {analysis.margin_result?.margin_pct?.toFixed(1)}%</>}
                </div>
              )}
              <Input
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
                placeholder="Subject (e.g. Why is my margin so low?)"
                className="text-sm"
                data-testid="support-subject-input"
              />
              <textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder="Describe your question or concern..."
                className="w-full p-2 border border-slate-200 rounded-md text-sm text-slate-800 resize-none h-24 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                data-testid="support-message-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setSupportModal(false)} className="flex-1 text-sm" data-testid="support-cancel-btn">Cancel</Button>
                <Button onClick={handleSubmitTicket} disabled={!supportSubject.trim() || !supportMessage.trim() || submittingTicket} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm" data-testid="support-submit-btn">
                  {submittingTicket ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />}
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Disclosure */}
      <p className="text-[10px] text-slate-400 text-center mt-6 pb-4" data-testid="ai-disclosure">
        Generated using deterministic commercial modelling and AI-assisted commentary. Not professional advice.
      </p>
    </AppLayout>
  );
}


/* ------------------------------------------------------------------ */
/* Decision Guidance Panel — decision-first layer on bid detail        */
/* ------------------------------------------------------------------ */
function DecisionGuidancePanel({ marginResult, riskAssessment, bidHealth, executiveCommentary, underbidFlags, sensitivityResult, bid }) {
  if (!marginResult) return null;

  const margin = marginResult.gross_margin_pct;
  const profit = marginResult.profit;
  const targetMin = 10; // general band
  const targetMax = 16;
  const targetMid = (targetMin + targetMax) / 2;

  // Determine decision
  let headline, riskLevel, riskColor, bgColor, borderColor;
  if (margin < 0) {
    headline = 'This bid will lose money';
    riskLevel = 'Critical — Loss-Making';
    riskColor = 'text-red-400';
    bgColor = 'bg-red-500/8';
    borderColor = 'border-red-500/25';
  } else if (margin < targetMin * 0.5) {
    headline = 'You are significantly underpricing this bid';
    riskLevel = 'High Margin Risk';
    riskColor = 'text-red-400';
    bgColor = 'bg-red-500/8';
    borderColor = 'border-red-500/25';
  } else if (margin < targetMin) {
    headline = 'You are likely underpricing this bid';
    riskLevel = 'Elevated Risk — Below Target';
    riskColor = 'text-amber-400';
    bgColor = 'bg-amber-500/8';
    borderColor = 'border-amber-500/25';
  } else {
    headline = 'This bid is commercially sound';
    riskLevel = 'Within Target Band';
    riskColor = 'text-emerald-400';
    bgColor = 'bg-emerald-500/8';
    borderColor = 'border-emerald-500/25';
  }

  // Calculate adjustment
  let adjustment = 0, adjustmentPct = 0, adjustedPrice = bid?.submitted_price || 0;
  const totalCost = bid?.submitted_price ? bid.submitted_price * (1 - margin / 100) : 0;
  if (margin < targetMid && totalCost > 0) {
    const targetPrice = totalCost / (1 - targetMid / 100);
    adjustment = Math.max(0, targetPrice - (bid?.submitted_price || 0));
    adjustmentPct = bid?.submitted_price ? (adjustment / bid.submitted_price * 100) : 0;
    adjustedPrice = targetPrice;
  }

  // Downside (10% overrun)
  const overrunCost = totalCost * 1.10;
  const overrunProfit = (bid?.submitted_price || 0) - overrunCost;

  return (
    <div className={`rounded-sm border p-5 ${bgColor} ${borderColor}`} data-testid="decision-guidance-panel">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className={`w-6 h-6 ${riskColor} flex-shrink-0 mt-0.5`} />
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Commercial Decision</p>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            {headline}
          </h2>
          <p className={`text-xs font-bold uppercase tracking-wider mt-0.5 ${riskColor}`}>{riskLevel}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white/60 rounded-sm p-3">
          <p className="text-[9px] uppercase tracking-wider text-slate-500">Margin Position</p>
          <p className="text-lg font-bold font-mono text-slate-900">{margin?.toFixed(1)}%</p>
          <p className="text-[10px] text-slate-500">Target: {targetMin}%–{targetMax}%</p>
        </div>
        <div className="bg-white/60 rounded-sm p-3">
          <p className="text-[9px] uppercase tracking-wider text-slate-500">Downside (10% Overrun)</p>
          <p className={`text-lg font-bold font-mono ${overrunProfit < 0 ? 'text-red-600' : 'text-slate-900'}`}>
            £{Math.round(overrunProfit).toLocaleString()}
          </p>
          <p className="text-[10px] text-slate-500">Profit after cost increase</p>
        </div>
        {adjustment > 0 ? (
          <div className="bg-white/60 rounded-sm p-3 border border-orange-200">
            <p className="text-[9px] uppercase tracking-wider text-orange-600">Recommended Increase</p>
            <p className="text-lg font-bold font-mono text-orange-600">+£{Math.round(adjustment).toLocaleString()}</p>
            <p className="text-[10px] text-slate-500">+{adjustmentPct.toFixed(1)}% to reach {targetMid}%</p>
          </div>
        ) : (
          <div className="bg-white/60 rounded-sm p-3">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Adjustment</p>
            <p className="text-lg font-bold font-mono text-emerald-600">None needed</p>
            <p className="text-[10px] text-slate-500">Pricing provides adequate buffer</p>
          </div>
        )}
      </div>

      {executiveCommentary?.recommendation && (
        <p className="text-sm text-slate-700 leading-relaxed italic border-t border-slate-200 pt-3">
          {executiveCommentary.recommendation}
        </p>
      )}
    </div>
  );
}
